import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientGate } from '@/components/ClientGate/ClientGate';
import { getClientIdFromReturnUrl, shouldHideLinks } from '@/lib/clientId';
import { RouteTitleSync } from '@/lib/title';
import styles from './MainLayout.module.css';
import TermsModal from '@/components/TermsModal/TermsModal';
import MaintenanceBanner from '@/components/MaintenanceBanner/MaintenanceBanner';

type Props = {
  children: React.ReactNode;
  returnUrl?: string;
  signedOut?: boolean;
  contentMaxWidth?: string;
};

type ContentStyle = React.CSSProperties & {
  '--content-max-width'?: string;
};

export default function MainLayout({ children, returnUrl, signedOut, contentMaxWidth }: Props) {
  const { t } = useTranslation();
  const [openTerms, setOpenTerms] = useState(false);
  const clientId = getClientIdFromReturnUrl(returnUrl) || undefined;
  const isMmb = clientId === 'mymb' || clientId === 'mymb-pkce';
  const hide = shouldHideLinks({ signedOut, clientId, returnUrl });

  const contentStyle: ContentStyle | undefined = contentMaxWidth
    ? { '--content-max-width': contentMaxWidth }
    : undefined;

  return (
    <div className={`${styles.root} auth-scope`} data-client={isMmb ? 'mmb' : 'default'}>
      <RouteTitleSync />
      <aside className={styles.left}>
        <div className={styles.hero} />
        <div className={styles.logoGrid}>
          <div className={styles.logoGroup + ' ' + styles.forMmb}>
            <a href="http://www.childrenshospital.org/centers-and-services/departments-and-divisions/department-of-otolaryngology-and-communication-enhancement">
              <Logo src="/images/logo-mmb-bch.svg" alt="Boston Children's Hospital" />
            </a>
            <a href="https://www.tobiidynavox.com">
              <Logo src="/images/logo-mmb-td.svg" alt="Tobii Dynavox" />
            </a>
          </div>

          <div className={styles.logoGroup + ' ' + styles.forDefault}>
            <a href="https://www.myboardmaker.com">
              <Logo src="/images/logo-mybm.svg" alt="myBoardmaker" />
            </a>
            <a href="https://www.tobiidynavox.com">
              <Logo src="/images/logo-td.svg" alt="Tobii Dynavox" />
            </a>
            <a href="https://www.mytobiidynavox.com">
              <Logo src="/images/logo-mytd.svg" alt="myTobiiDynavox" />
            </a>
            <a href="http://efunding.tobiidynavox.com/">
              <Logo src="/images/logo-tdf.svg" alt="TD Funding" />
            </a>
            <a href="https://www.mymessagebanking.com/">
              <Logo src="/images/logo-mymb.svg" alt="myMessageBanking" />
            </a>
          </div>
        </div>

        <div className={styles.leftFooter}>
          {!hide && (
            <>
              <a
                href="https://www.tobiidynavox.com/pages/privacy-notice"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('Privacy_policy').replace(/&nbsp;/g, '\u00A0')}
              </a>
              <button onClick={() => setOpenTerms(true)} className="btn btn-link">
                {t('Terms_and_Conditions').replace(/&nbsp;/g, '\u00A0')}
              </button>
            </>
          )}
          <span className={styles.copy}>© Tobii Dynavox {new Date().getFullYear()}</span>
        </div>
      </aside>

      <div className={styles.right} data-main-layout-scroll-root>
        <div className={styles.content} style={contentStyle}>
          <MaintenanceBanner />
          <ClientGate include={['mymb', 'mymb-pkce']} returnUrl={returnUrl}>
            <div className={styles.primaryLogo}>
              <img src="/images/logo-mymb-color.svg" alt="myMessageBanking" />
            </div>
          </ClientGate>

          {children}

          <footer className={styles.rightFooter}>
            {!hide && (
              <div className="sm:hidden">
                <p>
                  <a href="https://www.tobiidynavox.com/pages/privacy-notice">
                    {t('Privacy_policy').replace(/&nbsp;/g, '\u00A0')}
                  </a>
                </p>
                <p>
                  <button onClick={() => setOpenTerms(true)} className="btn btn-link">
                    {t('Terms_and_Conditions').replace(/&nbsp;/g, '\u00A0')}
                  </button>
                </p>
                <p className="muted">© Tobii Dynavox {new Date().getFullYear()}</p>
              </div>
            )}
          </footer>
          <TermsModal
            open={openTerms}
            onClose={() => setOpenTerms(false)}
            title={t('Terms_and_Conditions').replace(/&nbsp;/g, '\u00A0')}
            loadingMessage={t('Pagesets_Message_Loading')}
          />
        </div>
      </div>
    </div>
  );
}

function Logo({ src, alt }: { src: string; alt: string }) {
  return <img className={styles.logo} src={src} alt={alt} />;
}
